import React, { useMemo } from "react";
import {
  FormControl,
  Select,
  MenuItem,
  Stack,
  SelectChangeEvent,
} from "@mui/material";
import { SSText } from "src/components/base/components/SSText";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { pluralize } from "src/utils/utils";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

interface AttendanceSeatDropdownProps {
  min: number;
  value: number;
  max: number;
  isDisabled?: boolean;
  onChange: (value: number) => void;
}

export function AttendanceSeatDropdown(props: AttendanceSeatDropdownProps) {
  const theme = useSSTheme();

  // Generate seat count options
  const seatOptions = useMemo(() => {
    const options: number[] = [];

    if (props.max === 0) {
      return options;
    }

    for (let i = props.min; i <= props.max; i++) {
      options.push(i);
    }

    return options;
  }, [props.min, props.max]);

  const handleChange = (event: SelectChangeEvent<number>) => {
    const newValue = event.target.value as number;
    props.onChange(newValue);
  };

  const selectStyle = {
    backgroundColor: theme.mui.palette.grey[50],
    "& .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${theme.mui.palette.grey[300]}`,
      borderRadius: "8px",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.colors.teamPrimary,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.colors.teamPrimary,
    },
    "& .MuiSelect-select": {
      padding: "12px 16px",
      fontSize: "1rem",
      fontWeight: 600,
    },
  };

  return (
    <Stack width="100%">
      <FormControl
        fullWidth
        variant="outlined"
        disabled={props.isDisabled || seatOptions.length === 0}
      >
        <Select
          value={props.value}
          onChange={handleChange}
          IconComponent={KeyboardArrowDownRoundedIcon}
          sx={{
            ...selectStyle,
            opacity: props.isDisabled ? 0.5 : 1,
            pointerEvents: props.isDisabled ? "none" : "auto",
            cursor: props.isDisabled ? "not-allowed" : "pointer",
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 200,
                "& .MuiMenuItem-root": {
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  padding: "8px 16px",
                  "&:hover": {
                    backgroundColor: theme.mui.palette.action.hover,
                  },
                  "&.Mui-selected": {
                    backgroundColor: `${theme.colors.teamPrimary}20`,
                    color: theme.colors.teamPrimary,
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: `${theme.colors.teamPrimary}30`,
                    },
                  },
                },
              },
            },
          }}
        >
          {seatOptions.length > 0 ? (
            seatOptions.map((count) => (
              <MenuItem key={count} value={count}>
                {count} {pluralize("seat", count)}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled value="">
              No seats available
            </MenuItem>
          )}
        </Select>
      </FormControl>

      {/* Status indicator */}
      {/* <Stack alignItems="left" spacing={1}>
        <SSText
          variant="body2"
          fontWeight={500}
          textAlign="left"
          color={theme.mui.palette.text.secondary}
          fontSize="0.80rem"
        >
          Available: {props.max} {pluralize("seat", props.max)} maximum
        </SSText>
      </Stack> */}
    </Stack>
  );
}
